import React, { useState, ChangeEvent, useEffect } from "react";

import { AccountType, useAccount } from "@shared/contexts";

import { useNavigate } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { Field as FormField } from "@components/ui";


import AccountService from "@features/createaccount/service/AccountService";

import { PagePaths } from "@shared/constants";
import { ComposeURLWithGetQueryArguments } from "@shared/utils";

import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { t_createPassword } from "@i18n/usualTexts/t_createPassword";
import { t_help } from "@i18n/usualTexts/t_help";
import { cleanFormDataErrors } from "@shared/utils";
import { RegisterPasswordRequest } from "@features/createaccount/service/AccountRequest";
import { delay, intervalMiliseconds } from "@shared/utils";
import { useAuthError } from "@shared/hooks/useAuthError";

export interface FormData {
  password: {
    value: string;
    errors: Set<string>;
  };
  confirmPassword: {
    value: string;
    errors: Set<string>;
  };
}

const CreatePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle(t("signup.createPassword.pageTitle"));
  const [submitted, setSubmitted] = useState(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { accountData, setAccountData } = useAccount();
  const { handleRegisterError } = useAuthError();
  const [formData, setFormData] = useState<FormData>({
    password: {
      value: "",
      errors: new Set<string>(),
    },
    confirmPassword: {
      value: "",
      errors: new Set<string>(),
    },
  });

  return (
    <AuthenticationForm
      title={t(t_createPassword.title)}
      subtitle={t(t_createPassword.subtitle)}
      back={back}
      onSubmit={() => handleSubmit()}
      isLoading={isLoading}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
    >
      <FormField
        autofocus={true}
        name="password"
        type={showPassword ? "text" : "password"}
        required={true}
        value={formData.password.value}
        errors={formData.password.errors}
        onChange={handleFormData}
        submitted={submitted}
        toPresentErrors={true}
        showPasswordButton={false}
      >
        {t(t_createPassword.passwordPlaceholder)}
      </FormField>

      <FormField
        name="confirmPassword"
        type={showPassword ? "text" : "password"}
        required={true}
        value={formData.confirmPassword.value}
        errors={formData.confirmPassword.errors}
        onChange={handleFormData}
        submitted={submitted}
        toPresentErrors={true}
        showPasswordButton={false}
      >
        {t(t_createPassword.confirmPasswordPlaceholder)}
      </FormField>

      <div className="flex items-center gap-2">
        <input
          className="h-4 w-4 rounded border-gray-300"
          type="checkbox"
          id="showPasswordCheckbox"
          checked={showPassword}
          onChange={(e) => setShowPassword(e.target.checked)}
        />
        <label className="text-sm" htmlFor="showPasswordCheckbox">
          {t(t_help.showPasswordCheck)}
        </label>
      </div>
    </AuthenticationForm>
  );

  function handleFormData(e: ChangeEvent<HTMLInputElement>) {
    const { value, name } = e.target;

    formData[name as keyof FormData].value = value;
    setFormData({
      ...formData,
    });
  }

  function back() {
    setAccountData({
      ...accountData,
      step: 3,
    });

    navigate(ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.INSERTUSERNAME));
  }

  async function handleSubmit() {
    setSubmitted(true);
    setIsLoading(true);

    const step = 5;

    const newAccountData: AccountType = {
      ...accountData,
      step: step,
    };

    try {
      checkThisFormData();

      await post_and_navigate(newAccountData);
    } catch (e) {
      const error = JSON.parse(JSON.stringify(e));
      handleRegisterError(error, setServerErrorPopUp, frontExceptionFunction);
    } finally {
      setIsLoading(false);

      setFormData({
        ...formData,
      });

      await delay(1000);
      setSubmitted(false);
    }
  }

  async function post_and_navigate(newAccountData: AccountType) {
    const identityPostResponse = accountData.identityPostResponse;
    if (!identityPostResponse) {
      throw new Error(t("error.runtime.noIdentityResponse"));
    }

    const intervalMilisecondsValue = intervalMiliseconds(identityPostResponse);
    await delay(intervalMilisecondsValue);

    const passwordPostResponse = await RegisterPasswordRequest(
      formData.password.value,
      formData.confirmPassword.value,
      identityPostResponse.access_token,
      undefined
    );
    console.log(passwordPostResponse);

    // Update account data with the response
    const updatedAccountData: AccountType = {
      ...newAccountData,
      passwordPostResponse: passwordPostResponse,
    };

    setAccountData(updatedAccountData);

    navigate(ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.VERIFYEMAIL));
  }

  function checkThisFormData() {
    cleanFormDataErrors(formData);

    const accountService: AccountService = new AccountService(t);
    accountService.checkPassword(formData.password.value, "password");
    accountService.checkConfirmPassword(
      formData.confirmPassword.value,
      formData.password.value,
      "confirmPassword"
    );
  }

  function frontExceptionFunction(errorDescription: Array<string>) {
    formData[errorDescription[0] as keyof FormData].errors.add(
      errorDescription[1]
    );
  }
};

export default CreatePasswordPage;
